import type { CatalogItemDataFilterInput } from 'gql/graphql';
import type { SelectedFilters, DateRange, SortByProps } from 'uikit';
import { getTagLabelDefault } from 'uikit/getTagLabelDefault';

import * as R from 'ramda';

export type IQueryWhere =
    | undefined
    | (SelectedFilters & {
          searchString: string | undefined;
      });
export type IQueryOrder = { [key: string]: string }[];

const converter = {
    updatedAt(value: DateRange) {
        const [gte, lt] = value.map((date, idx) => {
            if (idx === 1 && date) {
                const nextDayDate = new Date(date);

                nextDayDate.setDate(nextDayDate.getDate() + 1);

                return nextDayDate.toISOString();
            }

            return date ? date.toISOString() : undefined;
        });

        return {
            gte,
            lt,
        };
    },
    type(value: { data: { value: string } }[]) {
        return { methodologies: { all: { in: value.map(({ data: { value: methodology } }) => methodology) } } };
    },
};

const getValueAnd = (fieldName: string, value: any) => {
    const objIn = {
        in: value.map(
            ({ data: { value: itemValue } }: { data: { value: { id?: number } } }) => itemValue?.id || itemValue,
        ),
    };

    return { [fieldName]: objIn };
};

const getRestWhere = (selectedFilters: SelectedFilters): CatalogItemDataFilterInput | undefined => {
    const predicates = Object.entries(selectedFilters)
        .filter(([, { selected, value }]) => selected.length && !R.isNil(value) && value.length)
        .map(([fieldName, { selected, value }]) =>
            converter[fieldName as keyof typeof converter]
                ? { [fieldName]: converter[fieldName as keyof typeof converter](value) }
                : getValueAnd(fieldName, value),
        );

    return predicates.length
        ? {
              and: predicates,
          }
        : undefined;
};

export const getWhere = (selectedFilters: IQueryWhere): CatalogItemDataFilterInput | undefined => {
    const { searchString, ...rest } = selectedFilters || {};

    const restWhereVariables = getRestWhere(rest);

    const stringWhere = searchString && {
        or: [
            {
                subsidiaryName: {
                    containsi: searchString,
                },
            },
            {
                name: {
                    containsi: searchString,
                },
            },
        ],
    };

    if (stringWhere && restWhereVariables && !R.isEmpty(restWhereVariables)) {
        return {
            and: [stringWhere, restWhereVariables],
        };
    }

    if (stringWhere) {
        return stringWhere;
    }

    if (!R.isEmpty(restWhereVariables)) {
        return restWhereVariables;
    }

    return undefined;
};

export const getCatalogListTagLabel = (id: string, name: string, filterValue: any) => {
    if (id === 'updatedAt') {
        return `${name} ${filterValue?.map((d: Date) => (d ? d.toLocaleDateString() : '')).join(' - ') ?? 'не задано'}`;
    }

    return getTagLabelDefault(id, name, filterValue);
};

export const sortCatalogListQueryOrder = (sortParams: SortByProps<any> | null) => {
    if (sortParams === null) {
        return [{ updatedAt: 'DESC' }];
    }
    const { sortOrder, sortingBy } = sortParams;

    return [{ [sortingBy]: sortOrder.toUpperCase() }];
};
